import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Table, Spin, message, Modal, Tag, InputNumber, Input } from "antd";
import {
  EditOutlined, DeleteOutlined, DollarOutlined,
  HistoryOutlined, EyeOutlined, PhoneOutlined, PrinterOutlined, RiseOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { apiClient } from "../../../shared/api/AllApi";
import { apiDelete } from "../../../utils/apiHelpers";
import { invalidateCatalog } from "../../../hooks/useCatalogData";
import { formatCurrency } from "../../../utils/formatters";
import { parseLocalizedName } from "../../../utils/localization";
import { readCachedSystemConfig, getPrintColors } from "../../../shared/lib/systemConfig";
import { formatOrderNo, formatCustomerPaymentNo } from "../../../shared/lib/documentNumbers";
import { PRINT_ADAPTIVE_CSS } from "../../purchasing/lib/printFarmerSettlement";
import { printHtml } from "../../../shared/lib/printHtml";
import usePermissions from "../../../hooks/usePermissions";
import { PageHeader, StatCard, T, cardStyle } from "../../../shared/ui/PageShell";
import useTablePagination from "../../../shared/ui/useTablePagination";

const CustomerView = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { pagination: ordersPagination } = useTablePagination();
  const { pagination: paymentsPagination } = useTablePagination();

  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loadingCustomer, setLoadingCustomer] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Quick "pay debt" modal (mirrors the vendor flow: amount + note, one step).
  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState(0);
  const [payNote, setPayNote] = useState("");
  const [paySaving, setPaySaving] = useState(false);

  useEffect(() => {
    apiClient.get(`/customers/${id}`)
      .then((res) => setCustomer(res.data))
      .catch(() => message.error(t("loadError", "Failed to load")))
      .finally(() => setLoadingCustomer(false));

    apiClient.get(`/customers/${id}/orders`)
      .then((res) => {
        // Endpoint returns { ..., orders: [...] } — not a bare array.
        const raw = res.data?.orders ?? res.data;
        setOrders(Array.isArray(raw) ? raw : []);
      })
      .catch(() => setOrders([]))
      .finally(() => setLoadingOrders(false));

    apiClient.get(`/customers/${id}/payments`)
      .then((res) => {
        // Endpoint returns { payments: [...] } with each row keyed as `amount`.
        const raw = res.data?.payments ?? res.data?.data ?? res.data;
        setPayments(Array.isArray(raw) ? raw : []);
      })
      .catch(() => setPayments([]))
      .finally(() => setLoadingPayments(false));
  }, [id, reloadKey]);

  const handleDelete = async () => {
    try {
      await apiDelete(`customers/${id}`);
      invalidateCatalog("customers");
      message.success(t("deleteSuccess", "Customer deleted successfully"));
      navigate("/customers");
    } catch {
      message.error(t("deleteError", "Error deleting customer"));
    }
  };

  const openPay = () => {
    const owed = parseFloat(customer?.total_owed || 0);
    setPayAmount(owed > 0 ? owed : 0);
    setPayNote("");
    setPayOpen(true);
  };

  // One-step payment: amount (+ optional note), straight to the API. No accountant,
  // no page navigation — same quick flow as the vendor payment.
  const submitPayment = async () => {
    const amt = parseFloat(payAmount || 0);
    if (!(amt > 0)) { message.error(t("vendorsPaymentInvalid", "Enter a valid amount")); return; }
    const previousOwed = parseFloat(customer?.total_owed || 0);
    const remainingAfter = Math.round(Math.max(0, previousOwed - amt) * 100) / 100;
    setPaySaving(true);
    try {
      const fd = new FormData();
      fd.append("id", id);
      fd.append("amount", String(amt));
      fd.append("payment_method", "cash");
      if (payNote) fd.append("sender", payNote);
      const res = await apiClient.post(`/customers/pay/${id}`, fd);
      message.success(t("vendorsPaymentSuccess", "Payment recorded"));
      setPayOpen(false);
      invalidateCatalog("customers");
      setReloadKey((k) => k + 1);
      const paymentId = res.data?.payment?.id;
      if (paymentId) {
        const q = new URLSearchParams({
          old: String(previousOwed),
          paid: String(amt),
          remain: String(remainingAfter),
          name: customer?.name || "",
        });
        window.open(
          `/customers/${id}/receipts/${paymentId}?${q.toString()}`,
          "_blank",
          "noopener,noreferrer"
        );
      }
    } catch (e) {
      message.error(e?.response?.data?.message || t("vendorsPaymentFailed", "Payment failed"));
    } finally {
      setPaySaving(false);
    }
  };

  if (loadingCustomer) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 320 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!customer) return null;

  const debt = parseFloat(customer.total_owed || 0);
  const totalOrdered = orders.reduce((a, o) => a + parseFloat(o.total_price || 0), 0);
  // Prefer the payments ledger for "how much was paid" — order.amount_paid alone
  // misses debt-clearing payments that only update customer.total_owed.
  const totalPaidFromPayments = payments.reduce((a, p) => {
    const n = parseFloat(p.amount ?? p.amount_paid ?? p.amount_main ?? 0);
    return a + (n > 0 ? n : 0);
  }, 0);
  const totalPaidFromOrders = orders.reduce((a, o) => a + parseFloat(o.amount_paid || 0), 0);
  const totalPaid = payments.length > 0 ? totalPaidFromPayments : totalPaidFromOrders;
  // Reconstruct what the debt was before payments still reflected in the balance.
  const previousDebt = Math.round((debt + totalPaid) * 100) / 100;
  const cfg = readCachedSystemConfig() || {};
  const mainCode = cfg.currency_code || "IQD";

  // Full customer account statement — orders, payments, and what they still owe us.
  const printStatement = () => {
    const colors = getPrintColors(cfg);
    const company = (cfg.invoice_company_name || "").trim() || cfg.site_name || "";
    const money = (v) => formatCurrency(v, mainCode);
    const esc = (x) => String(x ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const ordRows = orders.map((o) => {
      const rem = parseFloat(o.total_price || 0) - parseFloat(o.amount_paid || 0);
      return `<tr><td>${esc(o.document_no || formatOrderNo(o.id, o.created_at))}</td><td>${o.created_at ? dayjs(o.created_at).format("YYYY-MM-DD") : "-"}</td><td style="text-align:right">${money(o.total_price)}</td><td style="text-align:right">${money(o.amount_paid)}</td><td style="text-align:right"><b>${money(rem)}</b></td></tr>`;
    }).join("");
    const payRows = payments.map((p) => `<tr><td>${esc(p.document_no || formatCustomerPaymentNo(p.id, p.created_at))}</td><td>${p.created_at ? dayjs(p.created_at).format("YYYY-MM-DD") : "-"}</td><td style="text-align:right">${money(p.amount)}</td><td>${esc(p.sender || p.receiver || p.payment_method || "")}</td></tr>`).join("");
    printHtml(`<!doctype html><html><head><meta charset="utf-8"><style>
      ${PRINT_ADAPTIVE_CSS}
      body{font-family:'Segoe UI',Tahoma,Arial,sans-serif;color:${colors.primary};font-size:13px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
      h1{font-size:22px;margin:0;color:${colors.accent}} h2{font-size:15px;margin:18px 0 6px;border-bottom:1px solid ${colors.primary};padding-bottom:4px;color:${colors.accent}}
      table{width:100%;border-collapse:collapse;margin-top:4px} th,td{padding:6px 8px;border-bottom:1px solid ${colors.soft};text-align:left} th{font-size:11px;text-transform:uppercase;color:${colors.primary};background:${colors.soft}}
      .box{border:1px solid ${colors.primary};border-radius:8px;padding:12px 16px;margin-top:8px;background:${colors.soft}}
      @media print and (max-width:190mm), print and (max-height:180mm){
        h1{font-size:16px!important} h2{font-size:12px!important;margin:10px 0 4px!important}
        th,td{padding:4px 5px!important;font-size:10px!important} .box{padding:8px 10px!important}
      }
    </style></head><body>
      <div style="text-align:center;margin-bottom:10px"><h1>${esc(company)}</h1><div style="opacity:.7">${t("customerStatementTitle", "Customer Account Statement")}</div></div>
      <div style="display:flex;justify-content:space-between"><div><b>${esc(customer.name)}</b>${customer.phone_number ? "<br>" + esc(customer.phone_number) : ""}</div><div style="text-align:right;opacity:.7">${dayjs().format("YYYY-MM-DD HH:mm")}</div></div>
      <div class="box">
        <div style="display:flex;justify-content:space-between;padding:4px 0"><span>${esc(t("totalPrice", "Total Price"))}</span><b>${money(totalOrdered)}</b></div>
        <div style="display:flex;justify-content:space-between;padding:4px 0"><span>${esc(t("previousDebtLabel", "Previous Debt"))}</span><b>${money(previousDebt)}</b></div>
        <div style="display:flex;justify-content:space-between;padding:4px 0"><span>${esc(t("paidAmountLabel", "Amount Paid"))}</span><b style="color:${colors.accent}">-${money(totalPaid)}</b></div>
        <div style="display:flex;justify-content:space-between;padding-top:8px;font-size:16px;border-top:1px solid ${colors.primary};margin-top:6px"><b>${esc(t("remainingDebtLabel", "Remaining Debt"))}</b><b style="color:${colors.accent}">${money(debt)}</b></div>
      </div>
      <h2>${esc(t("orderHistory", "Order History"))}</h2>
      <table><thead><tr><th>${t("docNo", "No.")}</th><th>${t("date", "Date")}</th><th style="text-align:right">${t("totalPrice", "Total Price")}</th><th style="text-align:right">${t("amountPaid", "Amount Paid")}</th><th style="text-align:right">${t("remaining", "Remaining")}</th></tr></thead><tbody>${ordRows || `<tr><td colspan="5" style="text-align:center;opacity:.55">—</td></tr>`}</tbody></table>
      <h2>${esc(t("paymentHistory", "Payment History"))}</h2>
      <table><thead><tr><th>${t("docNo", "No.")}</th><th>${t("date", "Date")}</th><th style="text-align:right">${t("amountPaid", "Amount Paid")}</th><th>${t("note", "Note")}</th></tr></thead><tbody>${payRows || `<tr><td colspan="4" style="text-align:center;opacity:.55">—</td></tr>`}</tbody></table>
    </body></html>`);
  };

  const orderColumns = [
    {
      title: <b>{t("orderNo", "Order #")}</b>, key: "id", width: 140,
      render: (_, record) => (
        <Link to={`/orders/${record.id}/view`} style={{ fontWeight: 700 }}>
          {record.document_no || formatOrderNo(record.id, record.created_at)}
        </Link>
      ),
    },
    {
      title: <b>{t("totalPrice", "Total Price")}</b>, dataIndex: "total_price", key: "total_price",
      render: (v, r) => (
        <div>
          <b>{formatCurrency(v)}</b>
          {!(r.is_taxed === false || r.is_taxed === 0) && Number(r.commission_amount) > 0 && (
            <div style={{ fontSize: 11, color: "#d97706", fontWeight: 600 }}>
              +{formatCurrency(r.commission_amount)} {t("posTerminalCommission", "Commission")}
            </div>
          )}
        </div>
      ),
    },
    {
      title: <b>{t("remaining", "Remaining")}</b>, key: "remaining",
      render: (_, r) => {
        const rem = parseFloat(r.total_price || 0) - parseFloat(r.amount_paid || 0);
        return <span style={{ fontWeight: 700, color: rem > 0 ? T.red : T.green }}>{formatCurrency(rem)}</span>;
      },
    },
    {
      title: <b>{t("status", "Status")}</b>, key: "payment_status",
      render: (_, r) => {
        const paid = parseFloat(r.amount_paid || 0);
        const total = parseFloat(r.total_price || 0);
        const [label, color] = paid <= 0.0001
          ? [t("statusUnpaid", "Unpaid"), "red"]
          : (paid < total - 0.0001 ? [t("statusPartial", "Partial"), "orange"] : [t("statusPaid", "Paid"), "green"]);
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: <b>{t("date", "Date")}</b>, dataIndex: "created_at", key: "created_at",
      render: (v) => <span style={{ color: T.muted, fontSize: 12 }}>{v ? dayjs(v).format("DD/MM/YYYY") : "—"}</span>,
    },
    {
      title: "", key: "link", width: 56, align: "center",
      render: (_, record) => (
        <Link to={`/orders/${record.id}/view`}>
          <Button size="small" icon={<EyeOutlined />} style={{ borderRadius: 7, background: "#f1f5f9", borderColor: "#e2e8f0" }} />
        </Link>
      ),
    },
  ];

  // Expandable order rows: show exactly what was ordered and the amounts.
  const renderOrderItems = (record) => {
    const items = Array.isArray(record.items) ? record.items : [];
    if (!items.length) return <span style={{ color: T.muted }}>—</span>;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "4px 8px" }}>
        {items.map((it, ii) => {
          const name = parseLocalizedName(it.name, i18n.language, t("product", "Product"));
          const w = parseFloat(it.weight || 0);
          const qty = w > 0 ? `${w} ${t("kg", "kg")}` : it.quantity;
          return (
            <div key={it.id ?? ii} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: T.sub, gap: 12 }}>
              <span style={{ fontWeight: 600, color: T.text }}>{name} <span style={{ color: T.muted, fontWeight: 400 }}>× {qty}</span></span>
              <div style={{ display: "flex", gap: 16 }}>
                <span>{formatCurrency(it.single_price, mainCode)}</span>
                <b style={{ color: T.text }}>{formatCurrency(it.total_price, mainCode)}</b>
              </div>
            </div>
          );
        })}
        {!(record.is_taxed === false || record.is_taxed === 0) && Number(record.commission_amount) > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#d97706", marginTop: 6, paddingTop: 6, borderTop: `1px dashed ${T.border}` }}>
            <span style={{ fontWeight: 600 }}>{t("posTerminalCommission", "Commission")}</span>
            <b>+ {formatCurrency(record.commission_amount, mainCode)}</b>
          </div>
        )}
      </div>
    );
  };

  const paymentColumns = [
    { title: "#", key: "idx", width: 46, render: (_, __, i) => <span style={{ color: T.muted }}>{i + 1}</span> },
    {
      title: <b>{t("docNo", "No.")}</b>, key: "document_no", width: 140,
      render: (_, r) => (
        <span style={{ fontWeight: 700, fontFamily: "monospace", fontSize: 12 }}>
          {r.document_no || formatCustomerPaymentNo(r.id, r.created_at)}
        </span>
      ),
    },
    {
      title: <b>{t("amountPaid", "Amount Paid")}</b>, key: "amount",
      render: (_, r) => {
        // Tolerate either `amount` (payments endpoint) or `amount_paid`/`amount_main`.
        const n = parseFloat(r.amount ?? r.amount_paid ?? r.amount_main ?? 0);
        // Negative amounts are debt increases/adjustments, not real payments.
        return <b style={{ color: n < 0 ? T.red : T.green }}>{formatCurrency(n, mainCode)}</b>;
      },
    },
    {
      title: <b>{t("note", "Note")}</b>, key: "note",
      render: (_, r) => <span style={{ color: T.sub }}>{r.sender || r.receiver || r.note || r.payment_method || "—"}</span>,
    },
    {
      title: <b>{t("date", "Date")}</b>, dataIndex: "created_at", key: "created_at",
      render: (v) => <span style={{ color: T.muted, fontSize: 12 }}>{v ? dayjs(v).format("DD/MM/YYYY") : "—"}</span>,
    },
    {
      title: "", key: "print", width: 56, align: "center",
      render: (_, r) => (
        r?.id && (hasPermission("customers.payment") || hasPermission("customers.index")) ? (
          <Link to={`/customers/${id}/receipts/${r.id}`} target="_blank" rel="noreferrer">
            <Button size="small" type="text" icon={<PrinterOutlined />} title={t("print", "Print")} />
          </Link>
        ) : null
      ),
    },
  ];

  const headerActions = (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {hasPermission("customers.payment") && debt > 0 && (
        <Button type="primary" icon={<DollarOutlined />} style={{ borderRadius: 9 }} onClick={openPay}>
          {t("payDebt", "Pay Debt")}
        </Button>
      )}
      {hasPermission("maintenance.reset") && (
        <Link to={`/customers/${id}/adjust-balance`}>
          <Button icon={<RiseOutlined />} style={{ borderRadius: 9, borderColor: "#fed7aa", color: "#c2410c" }}>
            {t("increaseDebtTitle", "Increase Customer Debt")}
          </Button>
        </Link>
      )}
      {hasPermission("customers.index") && (
        <Button icon={<PrinterOutlined />} style={{ borderRadius: 9 }} onClick={printStatement}>
          {t("customerPrintStatement", "Print Statement")}
        </Button>
      )}
      {hasPermission("customers.edit") && (
        <Link to={`/customers/${id}/edit`}>
          <Button icon={<EditOutlined />} style={{ borderRadius: 9 }}>{t("edit", "Edit")}</Button>
        </Link>
      )}
      {hasPermission("customers.delete") && (
        <Button danger icon={<DeleteOutlined />} style={{ borderRadius: 9 }} onClick={() => setDeleteOpen(true)}>
          {t("delete", "Delete")}
        </Button>
      )}
    </div>
  );

  return (
    <div style={{ padding: 24, background: T.bg, minHeight: "100%" }}>
      <PageHeader
        breadcrumb={[t("customers", "Customers"), customer.name]}
        title={customer.name}
        subtitle={
          <span style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            {customer.phone_number && (
              <span style={{ color: T.sub }}><PhoneOutlined style={{ marginInlineEnd: 4 }} />{customer.phone_number}</span>
            )}
            {customer.type && (
              <span style={{
                background: "#f1f5f9", color: T.sub,
                padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600,
              }}>
                {customer.type === "farmer"
                  ? t("farmer", "Farmer")
                  : customer.type === "customer"
                    ? t("customer", "Customer")
                    : customer.type}
              </span>
            )}
          </span>
        }
        action={headerActions}
      />

      {/* Stats */}
      <div className="velan-stats-row">
        <StatCard icon={<HistoryOutlined style={{ color: "#2563eb" }} />} label={t("orders", "Orders")} value={orders.length} iconBg="#dbeafe" />
        <StatCard icon={<DollarOutlined style={{ color: "#16a34a" }} />} label={t("paymentHistory", "Payment History")} value={payments.length} iconBg="#dcfce7" />
        <StatCard
          icon={<DollarOutlined style={{ color: debt > 0 ? "#e11d48" : "#16a34a" }} />}
          label={t("totalOwed", "Total Owed")}
          value={formatCurrency(debt, mainCode)}
          iconBg={debt > 0 ? "#fee2e2" : "#dcfce7"}
          valueColor={debt > 0 ? T.red : T.green}
        />
      </div>

      {/* Account / credit-debt panel — previous debt, paid, and current remaining */}
      <div style={{ ...cardStyle, marginBottom: 16, padding: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: T.text, marginBottom: 12 }}>{t("customerAccount", "Account")}</div>
        <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", color: T.sub }}>
          <span>{t("totalPrice", "Total Price")}</span><b style={{ color: T.text }}>{formatCurrency(totalOrdered, mainCode)}</b>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", color: T.sub }}>
          <span>{t("previousDebtLabel", "Previous Debt")}</span><b style={{ color: T.text }}>{formatCurrency(previousDebt, mainCode)}</b>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", color: T.sub }}>
          <span>{t("paidAmountLabel", "Amount Paid")}</span><b style={{ color: T.green }}>-{formatCurrency(totalPaid, mainCode)}</b>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0 4px", marginTop: 6, borderTop: `1px solid ${T.border}`, fontSize: 16 }}>
          <b style={{ color: T.text }}>{t("remainingDebtLabel", "Remaining Debt")}</b>
          <b style={{ color: debt > 0 ? T.red : T.green }}>{formatCurrency(debt, mainCode)}</b>
        </div>
        {debt > 0 && hasPermission("customers.payment") && (
          <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button type="primary" icon={<DollarOutlined />} style={{ borderRadius: 9 }} onClick={openPay}>
              {t("customerClearDebt", "Clear / Pay Debt")}
            </Button>
          </div>
        )}
      </div>

      {/* Orders */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}` }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: T.text }}>{t("orderHistory", "Order History")}</span>
        </div>
        <Table
          size="small"
          loading={loadingOrders}
          dataSource={orders.map((o) => ({ ...o, key: o.id }))}
          columns={orderColumns}
          expandable={{
            expandedRowRender: renderOrderItems,
            rowExpandable: (r) => Array.isArray(r.items) && r.items.length > 0,
          }}
          pagination={{ ...ordersPagination, size: "small" }}
          scroll={{ x: 500 }}
        />
      </div>

      {/* Payments */}
      <div style={cardStyle}>
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}` }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: T.text }}>{t("paymentHistory", "Payment History")}</span>
        </div>
        <Table
          size="small"
          loading={loadingPayments}
          dataSource={payments.map((p, i) => ({ ...p, key: p.id ?? i }))}
          columns={paymentColumns}
          pagination={{ ...paymentsPagination, size: "small" }}
          scroll={{ x: 400 }}
        />
      </div>

      <Modal
        title={t("payDebt", "Pay Debt")} open={payOpen}
        onOk={submitPayment} onCancel={() => setPayOpen(false)}
        okText={t("purchaseOrdersSave", "Save")} cancelText={t("cancel", "Cancel")}
        confirmLoading={paySaving} centered destroyOnHidden
      >
        <div style={{ marginBottom: 6, color: T.sub }}>
          {t("previousDebtLabel", "Previous Debt")}: <b style={{ color: T.red }}>{formatCurrency(debt, mainCode)}</b>
        </div>
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 4 }}>{t("paidAmountLabel", "Amount Paid")}</div>
        <InputNumber
          min={0}
          style={{ width: "100%" }}
          value={payAmount}
          onChange={(v) => setPayAmount(v ?? 0)}
          autoFocus
        />
        <div style={{ marginTop: 10, color: T.sub }}>
          {t("remainingDebtLabel", "Remaining Debt")}:{" "}
          <b style={{ color: Math.max(0, debt - parseFloat(payAmount || 0)) > 0 ? T.red : T.green }}>
            {formatCurrency(Math.max(0, debt - parseFloat(payAmount || 0)), mainCode)}
          </b>
        </div>
        <div style={{ fontSize: 12, color: T.muted, margin: "12px 0 4px" }}>{t("note", "Note")}</div>
        <Input
          value={payNote}
          onChange={(e) => setPayNote(e.target.value)}
          placeholder={t("note", "Note")}
        />
      </Modal>

      <Modal
        title={t("confirmDelete", "Confirm Delete")} open={deleteOpen}
        onOk={handleDelete} onCancel={() => setDeleteOpen(false)}
        okText={t("yesDelete", "Yes, Delete")} cancelText={t("cancel", "Cancel")} centered
      >
        <p>{t("deleteConfirmation", "Are you sure you want to delete this customer?")}</p>
      </Modal>
    </div>
  );
};

export default CustomerView;
